import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InfoPage from '../InfoPage';
import { authFetch } from '../../utils/authFetch';
import { persistAuthResult } from '../../utils/authHelpers';
import { usePersona } from '../../contexts/PersonaContext';

const PERSONAS = [
  {
    id: 'creator',
    label: 'Creator',
    description: 'Access creator dashboards, collab requests, creator posts, and creator settings.',
  },
  {
    id: 'host',
    label: 'Host',
    description: 'Access host dashboards, properties, host posts, and hosting tools.',
  },
  {
    id: 'traveler',
    label: 'Traveler',
    description: 'Access itineraries and traveler-only travel content.',
  },
  {
    id: 'partner',
    label: 'Partner',
    description: 'Preferred Partners must apply before this add-on is unlocked.',
    applyOnly: true,
  },
];

const STATUS_COPY = {
  active: { label: 'Active', color: '#0a8a3f' },
  pending_checkout: { label: 'Waiting on checkout', color: '#b97400' },
  pending_application: { label: 'Application pending', color: '#b97400' },
  approved_unbilled: { label: 'Approved \u2014 awaiting billing', color: '#b97400' },
  cancelled: { label: 'Cancelled', color: '#6b7280' },
};

const PlanSettings = () => {
  const navigate = useNavigate();
  const { refreshPersonaFromStorage } = usePersona();
  const [loading, setLoading] = useState(true);
  const [savingPersona, setSavingPersona] = useState(null);
  const [access, setAccess] = useState({ roles: [], addons: [] });
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 767 : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const email = typeof window !== 'undefined' ? localStorage.getItem('postaway_email') || '' : '';

  const addonByPersona = useMemo(() => {
    const map = {};
    (access.addons || []).forEach((addon) => {
      map[addon.persona] = addon;
    });
    return map;
  }, [access.addons]);

  const loadAccess = async () => {
    if (!email) {
      navigate('/login');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/access/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || 'access_check_failed');
      setAccess(data);
    } catch (e) {
      setError(e.message || 'Unable to load plan settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      setInfo('Add-on purchase complete. Welcome aboard!');
      params.delete('checkout');
      const url = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
      window.history.replaceState({}, '', url);
    } else if (params.get('checkout') === 'cancelled') {
      setInfo('Checkout cancelled. No changes were made.');
      params.delete('checkout');
      const url = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
      window.history.replaceState({}, '', url);
    }
  }, []);

  const performAddonRequest = async (persona, action) => {
    const res = await authFetch('/api/persona/addon', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ persona, action }),
    });
    const data = await res.json();
    if (res.status === 409 && data.applyUrl) {
      navigate(data.applyUrl);
      return null;
    }
    if (res.status === 501 && data.error === 'addon_not_configured') {
      throw new Error(data.message || 'This add-on is not available yet.');
    }
    if (!res.ok || !data.ok) {
      throw new Error(data.error || 'addon_update_failed');
    }
    return data;
  };

  const updateAddon = async (persona, action) => {
    setSavingPersona(persona);
    setError('');
    setInfo('');
    try {
      const data = await performAddonRequest(persona, action);
      if (!data) return;

      if (data.checkoutUrl) {
        window.location.assign(data.checkoutUrl);
        return;
      }
      if (data.token || data.user) {
        persistAuthResult({
          email: data.user?.email,
          token: data.token,
          user: data.user,
        });
      }
      refreshPersonaFromStorage();
      await loadAccess();
      if (action === 'add') {
        setInfo(
          data.prorated
            ? `Added ${persona}. A prorated charge has been added to your subscription.`
            : `Added ${persona}.`
        );
      } else {
        setInfo(`Removed ${persona}.`);
      }
    } catch (e) {
      setError(e.message || 'Unable to update this add-on.');
    } finally {
      setSavingPersona(null);
      setConfirmRemove(null);
    }
  };

  const handlePrimaryClick = (persona, isActive) => {
    if (persona.applyOnly) {
      navigate('/partner-signup');
      return;
    }
    if (isActive) {
      setConfirmRemove(persona.id);
    } else {
      updateAddon(persona.id, 'add');
    }
  };

  const below = (
    <div
      style={{
        width: '100%',
        maxWidth: 920,
        margin: isMobile ? '24px auto 48px' : '64px auto',
        padding: isMobile ? '0 16px' : '0 20px',
      }}
    >
      <h1
        style={{
          color: '#0f084b',
          marginBottom: 12,
          fontSize: isMobile ? 28 : undefined,
          lineHeight: isMobile ? 1.2 : undefined,
        }}
      >
        Plan & Add-ons
      </h1>
      <p
        style={{
          color: '#0f084b',
          opacity: 0.75,
          marginBottom: 20,
          fontSize: isMobile ? 14 : undefined,
        }}
      >
        Manage the profile types available under your single Postaway login.
      </p>

      <div
        style={{
          background: '#eef2ff',
          border: '1px solid #c7d2fe',
          borderRadius: 12,
          padding: isMobile ? 14 : 16,
          marginBottom: isMobile ? 16 : 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <strong style={{ color: '#0f084b', fontSize: isMobile ? 15 : undefined }}>
          Already have a separate account at another email?
        </strong>
        <span style={{ color: '#0f084b', opacity: 0.85, fontSize: isMobile ? 14 : undefined }}>
          Adding a persona here creates a brand-new, empty profile on this account. To bring an existing
          account's data over (properties, posts, subscriptions, messages, etc.), use Combine Accounts.
        </span>
        <div>
          <button
            type="button"
            className="button"
            onClick={() => navigate('/combine-accounts')}
            style={isMobile ? { width: '100%' } : undefined}
          >
            Combine accounts
          </button>
        </div>
      </div>

      {info && (
        <div
          style={{
            color: '#0a8a3f',
            background: '#ecfdf5',
            border: '1px solid #a7f3d0',
            padding: 12,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {info}
        </div>
      )}
      {error && (
        <div
          style={{
            color: '#dc1d08',
            background: '#fff1f2',
            border: '1px solid #fecdd3',
            padding: 12,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div>Loading...</div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {PERSONAS.map((persona) => {
            const addon = addonByPersona[persona.id];
            const hasRole = (access.roles || []).includes(persona.id);
            const status = addon?.status;
            // An add-on only counts as "active" if its status is active AND the
            // user still has the role. A cancelled persona_addons row that
            // hasn't yet had its role cleared shouldn't keep the card stuck on
            // "Remove".
            const active = (status === 'active' && hasRole) || (!status && hasRole);
            const pendingCheckout = status === 'pending_checkout' && !hasRole;
            const pendingApplication = status === 'pending_application';
            const disabled = savingPersona === persona.id;
            // Hide the badge when the user has fully removed the persona ?
            // showing "Cancelled" next to an "Add new" button is confusing.
            const showStatusBadge =
              status && status !== 'cancelled' && (active || pendingCheckout || pendingApplication);
            const statusMeta = showStatusBadge ? STATUS_COPY[status] : null;

            let primaryLabel;
            if (persona.applyOnly) {
              primaryLabel = active ? 'Partner Active' : pendingApplication ? 'Application pending' : 'Apply';
            } else if (active) {
              primaryLabel = 'Remove';
            } else if (pendingCheckout) {
              primaryLabel = 'Resume checkout';
            } else {
              primaryLabel = 'Add new (start fresh)';
            }

            return (
              <div
                key={persona.id}
                style={{
                  background: '#fff',
                  border: '1px solid #e6e8ec',
                  borderRadius: 16,
                  padding: isMobile ? 16 : 20,
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between',
                  gap: isMobile ? 12 : 16,
                  alignItems: isMobile ? 'stretch' : 'center',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      marginBottom: 6,
                      flexWrap: 'wrap',
                    }}
                  >
                    <h3
                      style={{
                        color: '#0f084b',
                        margin: 0,
                        fontSize: isMobile ? 18 : undefined,
                      }}
                    >
                      {persona.label}
                    </h3>
                    {statusMeta && (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: statusMeta.color,
                          background: '#f8fafc',
                          border: `1px solid ${statusMeta.color}33`,
                          padding: '2px 8px',
                          borderRadius: 999,
                        }}
                      >
                        {statusMeta.label}
                      </span>
                    )}
                  </div>
                  <p
                    style={{
                      color: '#0f084b',
                      opacity: 0.7,
                      margin: 0,
                      fontSize: isMobile ? 14 : undefined,
                      lineHeight: 1.45,
                    }}
                  >
                    {persona.description}
                  </p>
                  {!active && !pendingCheckout && !persona.applyOnly && (
                    <div style={{ marginTop: 8 }}>
                      <p
                        style={{
                          color: '#0f084b',
                          opacity: 0.55,
                          margin: 0,
                          fontSize: 13,
                          lineHeight: 1.45,
                        }}
                      >
                        Adding starts a new, empty {persona.label.toLowerCase()} profile.
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate('/combine-accounts')}
                        style={{
                          display: 'inline-block',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          marginTop: 4,
                          color: '#3730a3',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          fontSize: 13,
                          textAlign: 'left',
                        }}
                      >
                        Have an existing {persona.label.toLowerCase()} account? Combine accounts instead.
                      </button>
                    </div>
                  )}
                </div>
                <button
                  className={active || pendingCheckout ? 'button-stroke' : 'button'}
                  type="button"
                  disabled={disabled}
                  onClick={() => handlePrimaryClick(persona, active)}
                  style={isMobile ? { width: '100%' } : undefined}
                >
                  {disabled ? 'Saving...' : primaryLabel}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {confirmRemove && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 8, 75, 0.45)',
            display: 'flex',
            alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: isMobile ? 0 : 16,
          }}
          onClick={() => setConfirmRemove(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              padding: isMobile ? 20 : 24,
              borderRadius: isMobile ? '16px 16px 0 0' : 16,
              maxWidth: 460,
              width: isMobile ? '100%' : '90%',
              boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              maxHeight: isMobile ? '90vh' : undefined,
              overflowY: 'auto',
            }}
          >
            <h3
              style={{
                color: '#0f084b',
                marginTop: 0,
                fontSize: isMobile ? 18 : undefined,
              }}
            >
              Remove the {confirmRemove} add-on?
            </h3>
            <p
              style={{
                color: '#0f084b',
                opacity: 0.8,
                fontSize: isMobile ? 14 : undefined,
                lineHeight: 1.5,
              }}
            >
              You'll lose access to {confirmRemove} dashboards and tools, and any active{' '}
              {confirmRemove} subscription item will be cancelled. Historical data stays in place but is
              hidden until you re-add the persona.
            </p>
            <p
              style={{
                color: '#0f084b',
                opacity: 0.65,
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              Trying to combine this with another email instead?{' '}
              <button
                type="button"
                onClick={() => {
                  setConfirmRemove(null);
                  navigate('/combine-accounts');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#3730a3',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: 13,
                }}
              >
                Use Combine Accounts.
              </button>
            </p>
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column-reverse' : 'row',
                justifyContent: 'flex-end',
                gap: isMobile ? 8 : 12,
                marginTop: 16,
              }}
            >
              <button
                type="button"
                className="button-stroke"
                onClick={() => setConfirmRemove(null)}
                disabled={savingPersona === confirmRemove}
                style={isMobile ? { width: '100%' } : undefined}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button"
                onClick={() => updateAddon(confirmRemove, 'remove')}
                disabled={savingPersona === confirmRemove}
                style={isMobile ? { width: '100%' } : undefined}
              >
                {savingPersona === confirmRemove ? 'Removing...' : 'Yes, remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return <InfoPage title="" below={below} disableStamps />;
};

export default PlanSettings;
